import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  QApprovalRequestAlert,
  QBackLink,
  QBox,
  QButtonGroup,
  QCreatedAtMetadata,
  QFlex,
  QHeader,
  QHorizontalMetadata,
  QLastModifiedMetadata,
  QOpenPropertiesPanelButton,
  QSpacer,
  QTag,
  QTitle,
  QVersionMetadata,
} from '@qualio/ui-components';
import { Form, FormMarkAsReviewed } from '../../../types';
import { variantColorMap } from '../../../components/FormStatusBadge';
import * as DisplayStrings from '../../../displayStrings';
import { HeaderButtonGroup } from './HeaderButtonGroup/HeaderButtonGroup';
import DigitalSignatureModal from '../../../components/DigitalSignatureModal/DigitalSignatureModal';
import { useEventFormParams, useFormApproval, useMarkAsReviewed } from '../../../hooks';
import ReviewRequestBanner from '../../EventStepForm/components/ReviewRequestBanner';
import MarkAsReviewedModal from '../../EventStepForm/components/MarkAsReviewedModal';

type EventFormViewHeaderProps = {
  data: Form;
  isEditMode: boolean;
  showApprovalRequestAlert: boolean;
  showReviewRequestAlert: boolean;
  saveForm: (e?: React.BaseSyntheticEvent<object, any, any> | undefined) => Promise<void>;
  isUpdateFormLoading: boolean;
  isNoQualityApprover: boolean;
};

export const EventFormViewHeader: React.FC<EventFormViewHeaderProps> = ({
  data,
  isEditMode = false,
  showApprovalRequestAlert = false,
  showReviewRequestAlert = false,
  saveForm,
  isUpdateFormLoading,
  isNoQualityApprover = false,
}) => {
  const { t } = useTranslation();
  const { eventId, stepId, formId } = useEventFormParams();
  const [showApproveDeclineModal, setShowApproveDeclineModal] = useState<'approve' | 'decline' | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const { mutateAsync: formApprovalMutate } = useFormApproval(eventId, stepId, formId);
  const { mutateAsync: markAsReviewedMutate, isLoading: isMarkAsReviewedLoading } = useMarkAsReviewed(
    eventId,
    stepId,
    formId,
  );
  const backLink = isEditMode ? `/events/${eventId}/steps/${stepId}/forms/${formId}` : `/events/${eventId}`;
  const isDeclineFlow = showApproveDeclineModal === 'decline';

  const handleApprovalRequestActionClick = useCallback(
    async (payload: { email: string; password: string; comment: string }) => {
      const isApprove = showApproveDeclineModal === 'approve';
      await formApprovalMutate({ ...payload, isApprove });
      setShowApproveDeclineModal(null);
    },
    [formApprovalMutate, setShowApproveDeclineModal, showApproveDeclineModal],
  );

  const handleMarkReviewActionClick = useCallback(
    async (payload: FormMarkAsReviewed) => {
      await markAsReviewedMutate(payload);
      setShowReviewModal(false);
    },
    [markAsReviewedMutate, setShowReviewModal],
  );

  return (
    <>
      <QFlex>
        <QBox lineHeight="32px">
          <QBackLink href={backLink}>{t('common.labels.back')}</QBackLink>
        </QBox>
        <QSpacer />
        <QOpenPropertiesPanelButton />
      </QFlex>
      <QHeader showDivider={true}>
        <QTitle data-cy="event-form-header-title">{data.title}</QTitle>
        <QButtonGroup>
          <HeaderButtonGroup
            data={data}
            isEditMode={isEditMode}
            eventId={Number(eventId)}
            saveForm={saveForm}
            isUpdateFormLoading={isUpdateFormLoading}
            isNoQualityApprover={isNoQualityApprover}
          />
        </QButtonGroup>
        <QHorizontalMetadata>
          <QTag data-cy="form-status" variantColor={variantColorMap[data.status]}>
            {DisplayStrings.EventFormStatuses[data.status]}
          </QTag>
          <QVersionMetadata data-cy="form-version" value={data.version} />
          <QCreatedAtMetadata data-cy="form-created-at" value={new Date(data.created_at * 1000)} />
          <QLastModifiedMetadata data-cy="form-modified-at" value={new Date(data.updated_at * 1000)} />
        </QHorizontalMetadata>
        {showApprovalRequestAlert && (
          <QApprovalRequestAlert
            onApprove={() => setShowApproveDeclineModal('approve')}
            onReject={() => setShowApproveDeclineModal('decline')}
          />
        )}
      </QHeader>
      {showReviewRequestAlert && (
        <QBox mt="20px">
          <ReviewRequestBanner markAsReviewedButtonClick={() => setShowReviewModal(true)} />
        </QBox>
      )}
      {showReviewModal && (
        <MarkAsReviewedModal
          isOpen={true}
          isSubmitBtnLoading={isMarkAsReviewedLoading}
          onSubmit={handleMarkReviewActionClick}
          onClose={() => setShowReviewModal(false)}
        />
      )}
      {showApproveDeclineModal && (
        <DigitalSignatureModal
          isOpen={true}
          setIsOpen={() => setShowApproveDeclineModal(null)}
          onSave={handleApprovalRequestActionClick}
          isCommentRequired={isDeclineFlow}
          isSubmitButtonDestructive={isDeclineFlow}
          inputTexts={
            isDeclineFlow
              ? {
                  headingText: t('events.actions.declineStep'),
                  subText: t('events.modals.declineStepSubText'),
                  submitButtonText: t('common.labels.decline'),
                }
              : {
                  headingText: t('events.actions.approveStep'),
                  subText: t('events.modals.approveStepSubText'),
                  submitButtonText: t('common.labels.approve'),
                }
          }
          displayToast={false}
          isApprovalFlow={!isDeclineFlow}
        />
      )}
    </>
  );
};
